'use client'

import type { ServiceCenterDesktop } from './use-service-center-desktop'

/**
 * The phone window: a small Atlas window of its own (like Zoom's) that owns
 * the agent's phone. Only it runs the Twilio Device, the agent stream and the
 * ring alerts; every Atlas tab shows the same panel as a remote control of
 * it, so reloading Atlas (agents do, to see device status changes) never
 * touches a call. Tabs and the window are the same origin and talk over the
 * BroadcastChannel below.
 *
 * Kept free of the phone's own imports: the root layout's provider reads
 * the path helpers here.
 */

/** The window's name, so a second Launch Phone focuses the open one. */
const PHONE_WINDOW_NAME = 'atlas-phone'
const PHONE_WINDOW_FEATURES = 'popup,width=420,height=760'

const PHONE_WINDOW_PATH = '/phone'

export function isPhoneWindowPath(pathname: string | null): boolean {
  return pathname !== null && /^\/phone\/?$/.test(pathname)
}

/**
 * Opens the phone window, or brings the open one forward without touching
 * it: opening a named window with a URL would navigate it, reloading the
 * phone (and, after the close prompt, dropping a call). Must run in a click,
 * since browsers block popups opened any other way. False when blocked.
 */
export function openPhoneWindow(): boolean {
  // An empty URL returns the window of that name if this tab can reach it,
  // else a new blank popup, which is then sent to the phone page.
  const opened = window.open('', PHONE_WINDOW_NAME, PHONE_WINDOW_FEATURES)
  if (!opened) return false
  let blank = true
  try {
    blank = opened.location.href === 'about:blank'
  } catch {
    // Not readable: not ours to navigate.
    blank = false
  }
  if (blank) opened.location.href = PHONE_WINDOW_PATH
  opened.focus()
  launched = opened
  return true
}

/** The window this tab opened, if it did; tabs that did not cannot reach it. */
let launched: Window | null = null

/**
 * Brings the phone window forward when this tab can reach it (it opened
 * it), so a browser prompt shown there (the microphone) is seen. Opening by
 * name is not used: it would create a blank window when there is none.
 */
export function focusPhoneWindow(): void {
  try {
    if (launched && !launched.closed) launched.focus()
  } catch {
    // Closed or unreachable; nothing to bring forward.
  }
}

export const PHONE_WINDOW_CHANNEL = 'sc:phone:window'

/** What the window publishes: the desktop without its functions. */
export type PhoneSnapshot = Omit<
  ServiceCenterDesktop,
  'device' | 'clearOffer'
> & {
  device: Pick<
    ServiceCenterDesktop['device'],
    'status' | 'error' | 'active' | 'micBlocked'
  >
}

/** What a tab may ask the window to do; each is the desktop's own function. */
export type PhoneCommand =
  | { name: 'accept'; callSid?: string }
  | { name: 'reject'; callSid?: string }
  | { name: 'hangup'; callSid?: string }
  | { name: 'toggleMute' }
  | { name: 'dial'; to: string }
  | { name: 'primeMicrophone' }
  | { name: 'beforeAvailable' }
  | { name: 'clearOffer'; callId?: string }

export type PhoneWindowMessage =
  /** The window's phone, sent on every change and in answer to `hello`. */
  | { type: 'state'; snapshot: PhoneSnapshot }
  /**
   * The window is open but has no phone to show (still loading the agent,
   * or this account takes no calls).
   */
  | { type: 'none' }
  /** The window is going away (closed or reloading). */
  | { type: 'closed' }
  /** A tab opened and wants the current state. */
  | { type: 'hello' }
  | { type: 'command'; id: string; command: PhoneCommand }
  | {
      type: 'reply'
      id: string
      ok: boolean
      value?: string | null
      error?: string
    }

export function snapshotOf(desktop: ServiceCenterDesktop): PhoneSnapshot {
  const { device, clearOffer: _clearOffer, ...rest } = desktop
  return {
    ...rest,
    device: {
      status: device.status,
      error: device.error,
      active: device.active,
      micBlocked: device.micBlocked
    }
  }
}
